import { RemoteTable } from '@neo/blocks/data-table/data-table'
import type { ExtractedTableData } from '@neo/blocks/data-table/types'
import type { ApiResponse } from '@shared/types'
import type { AxiosError } from 'axios'
import { useMemo } from 'react'
import { settingsService } from 'v2/shared/services/settings-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { getAPIConsoleQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RPlivoNumbers } from 'v2/types/api/settings'
import { internationalFormat } from 'v2/utils'
import { errorToast, successToast } from 'v2/utils/toasts'
import {
  useUnlinkNumberFromApplicationMutation,
  useUnlinkNumberFromTrunkMutation,
} from '../queries'
import { getLinkedNumbersColumns } from './linked-numbers-table.config'

interface LinkedNumbersTableProps {
  applicationId: number | string | undefined
  applicationName?: string
  isTrunk?: boolean
}

const extractTableData = (
  data: RPlivoNumbers,
): ExtractedTableData<RPlivoNumbers['objects'][0]> => ({
  data: data.objects,
  totalCount: data.meta.total_count,
  hasNext: !!data.meta.next,
  hasPrevious: !!data.meta.previous,
})

export const LinkedNumbersTable = ({
  applicationId,
  applicationName,
  isTrunk = false,
}: LinkedNumbersTableProps) => {
  const { mutate: unlinkFromApp } = useUnlinkNumberFromApplicationMutation(applicationId)
  const { mutate: unlinkFromTrunk } = useUnlinkNumberFromTrunkMutation(applicationId)

  const handleUnlink = useMemo(
    () => (phoneNumber: string) => {
      const formattedNumber = internationalFormat(phoneNumber)
      const mutate = isTrunk ? unlinkFromTrunk : unlinkFromApp
      const entityType = isTrunk ? 'trunk' : 'application'
      mutate(phoneNumber, {
        onSuccess: () => {
          successToast({
            title: 'Success',
            description: `Number ${formattedNumber} has been unlinked${
              applicationName ? ` from ${entityType} ${applicationName}` : ''
            } successfully`,
          })
        },
        onError: (error) => {
          const errorText = getErrorText(error as AxiosError<ApiResponse<unknown>>)
          errorToast({
            title: 'Failed to unlink number',
            description:
              errorText || 'An error occurred while unlinking the number. Please try again.',
          })
        },
      })
    },
    [isTrunk, unlinkFromApp, unlinkFromTrunk, applicationName],
  )

  const columns = useMemo(
    () => getLinkedNumbersColumns(handleUnlink, applicationName, isTrunk),
    [handleUnlink, applicationName, isTrunk],
  )

  const fetchNumbers = useMemo(
    () => async (params: Record<string, string>) => {
      const response = await settingsService.listNumbers({
        ...params,
        ...(isTrunk
          ? { trunk_id: applicationId?.toString() || '' }
          : { app_id: applicationId?.toString() || '' }),
      })
      return {
        ...response,
        data: response.data as RPlivoNumbers,
      }
    },
    [applicationId, isTrunk],
  )

  return (
    <div className="mt-4 space-y-1">
      <div className="flex items-center justify-between">
        <h3 className="text-p-500">Linked Numbers</h3>
      </div>
      <div className="border rounded-lg overflow-hidden [&_thead]:bg-bg2 [&_thead_th]:bg-bg2 [&_thead_th:hover]:bg-bg2 [&_thead_th]:border-b-0 [&_>div.flex.w-full.items-start]:hidden [&_table]:border-0 [&_tbody_tr]:border-0">
        <RemoteTable
          idKey="number"
          enableOffsetMode={true}
          ignoreUrlPagination={true}
          showLoader={true}
          defaultPageSize={5}
          queryKey={[
            ...(isTrunk
              ? getAPIConsoleQueryKeys().inboundTrunks
              : getAPIConsoleQueryKeys().xmlApplications),
            'numbers',
            String(applicationId),
          ]}
          columns={columns}
          fetchService={fetchNumbers}
          extractData={extractTableData}
        />
      </div>
    </div>
  )
}
